import { FC, ReactElement } from 'react';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ChevronRight,
  GridViewOutlined,
  CategoryOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Category } from '../../../app/services/api/orderManagementClient';
import { resolveMaterialIcon } from '../../orderly-for-distributors/ItemTags/components/itemTagUtils';

export const getCategoryIcon = (iconKey?: string): ReactElement => {
  if (!iconKey) return <CategoryOutlined sx={{ fontSize: 20 }} />;
  const IconComponent = resolveMaterialIcon(iconKey);
  return <IconComponent sx={{ fontSize: 20 }} />;
};

type Props = {
  categories: Category[];
  loading?: boolean;
  selectedCategoryId: number | null;
  fixedHeight?: number;
  // eslint-disable-next-line no-unused-vars
  onCategorySelect: (value: number | null) => void;
};

const CATEGORY_ROW_HEIGHT = 52;

const CategorySidebar: FC<Props> = ({ categories, loading, selectedCategoryId, fixedHeight, onCategorySelect }) => {
  const theme = useTheme();
  const { t } = useTranslation('storefront');
  const visibleCategories = categories.filter((cat) => cat.showInNavigation);
  const categoryIds = new Set(visibleCategories.map((c) => c.id));
  const hasChildren = (catId: number) => visibleCategories.some((c) => c.parentId === catId && categoryIds.has(c.id));

  return (
    <Box
      sx={{
        width: { xs: '100%', lg: 280 },
        minWidth: { xs: 0, lg: 280 },
        height: { xs: 'auto', lg: fixedHeight ? `${fixedHeight}px` : 'auto' },
        backgroundColor: theme.palette.background.paper,
        borderRadius: '5px',
        border: `1px solid ${theme.palette.custom.gray[200]}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <ListItemButton
        selected={selectedCategoryId === null}
        onClick={() => onCategorySelect(null)}
        sx={{
          py: 0,
          pr: 2,
          pl: 5,
          minHeight: CATEGORY_ROW_HEIGHT,
          height: { xs: 'auto', lg: CATEGORY_ROW_HEIGHT },
          maxHeight: { xs: 'none', lg: CATEGORY_ROW_HEIGHT },
          boxSizing: 'border-box',
          minWidth: { xs: 200, lg: 'auto' },
          borderRight: { xs: `1px solid ${theme.palette.custom.gray[100]}`, lg: 'none' },
          borderBottom: `1px solid ${theme.palette.custom.gray[100]}`,
          transition: 'background-color 0.2s ease',
          '&:hover': {
            backgroundColor: theme.palette.custom.gray[50],
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 30 }}>
          <GridViewOutlined sx={{ fontSize: 20, color: theme.palette.custom.gray[700] }} />
        </ListItemIcon>
        <ListItemText
          sx={{ my: 0 }}
          primary={
            <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.25 }}>
              {t('allCategories')}
            </Typography>
          }
        />
      </ListItemButton>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: { xs: 'hidden', lg: 'auto' },
          overflowX: { xs: 'auto', lg: 'hidden' },
          pr: { xs: 0, lg: 0.5 },
          overscrollBehavior: 'contain',
          scrollbarGutter: 'stable',
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.custom.gray[300]} transparent`,
          '&::-webkit-scrollbar': {
            width: { xs: 6, lg: 8 },
            height: { xs: 6, lg: 8 },
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
            margin: '8px 0',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.custom.gray[300],
            borderRadius: 999,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            minHeight: 32,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: theme.palette.custom.gray[400],
          },
        }}
      >
        <List
          disablePadding
          sx={{
            display: { xs: 'flex', lg: 'block' },
            flexDirection: { xs: 'row', lg: 'column' },
            justifyContent: 'flex-start',
            alignItems: 'stretch',
            width: { xs: 'max-content', lg: 'auto' },
            minWidth: { xs: '100%', lg: 'auto' },
            mt: 0,
            pt: 0,
            pb: 0,
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && visibleCategories.map((cat) => {
            const isSelected = selectedCategoryId === cat.id;
            return (
              <ListItemButton
                key={cat.id}
                selected={isSelected}
                onClick={() => onCategorySelect(isSelected ? null : cat.id)}
                sx={{
                  py: 0,
                  pr: 2,
                  pl: 5,
                  minHeight: CATEGORY_ROW_HEIGHT,
                  height: { xs: 'auto', lg: CATEGORY_ROW_HEIGHT },
                  maxHeight: { xs: 'none', lg: CATEGORY_ROW_HEIGHT },
                  boxSizing: 'border-box',
                  minWidth: { xs: 200, lg: 'auto' },
                  borderRight: { xs: `1px solid ${theme.palette.custom.gray[100]}`, lg: 'none' },
                  borderBottom: `1px solid ${theme.palette.custom.gray[100]}`,
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.custom.gray[50],
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 30, color: isSelected ? theme.palette.custom.gray[700] : theme.palette.custom.gray[600] }}>
                  {getCategoryIcon(cat.iconKey)}
                </ListItemIcon>
                <ListItemText
                  sx={{ my: 0 }}
                  primary={
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.25 }}>
                      {cat.name}
                    </Typography>
                  }
                />
                {hasChildren(cat.id) && <ChevronRight sx={{ fontSize: 18, color: theme.palette.custom.gray[300] }} />}
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default CategorySidebar;
